<template>
  <div
    class="cf-closeout"
    :class="{ 'cf-closeout--overriding': overriding }"
    role="alert"
    data-cy="cf-closeout-block"
  >
    <i
      class="cf-closeout__icon fa-regular"
      :class="overriding ? 'fa-circle-check' : 'fa-exclamation-triangle'"
    ></i>
    <div class="cf-closeout__body">
      <!-- "this visit": the gate queries by visit now, so every blocker listed
           either belongs to the visit being completed or is a job-level form that
           applies to all of them. A blocker from a different visit cannot appear. -->
      <p class="cf-closeout__headline" data-cy="cf-closeout-headline">
        {{ headline }}
      </p>

      <div
        v-for="group in groups"
        :key="group.id"
        class="cf-closeout__group"
        :data-cy="`cf-closeout-gap-${group.id}`"
      >
        <div class="cf-closeout__group-head">
          <span class="cf-closeout__name">{{ group.name }}</span>
          <span
            class="cf-closeout__count"
            :data-cy="`cf-closeout-progress-${group.id}`"
            >{{ group.answered }} / {{ group.total }} required</span
          >
        </div>

        <!-- Fully answered but unsubmitted: it blocks on the submit, not on an
             answer, so the list is legitimately empty and needs saying. Linked
             because the checklist is where the submit button lives — the one
             action that clears this. -->
        <p
          v-if="!group.items.length"
          class="cf-closeout__awaiting"
          :data-cy="`cf-closeout-awaiting-${group.id}`"
        >
          <a
            v-if="group.allHref"
            :href="group.allHref"
            class="cf-closeout__link"
            :data-cy="`cf-closeout-open-${group.id}`"
            >Answered in full — open it to submit.</a
          >
          <span v-else>Answered in full — waiting to be submitted.</span>
        </p>

        <ul v-else class="cf-closeout__items">
          <li
            v-for="item in group.visible"
            :key="item.key"
            class="cf-closeout__item"
          >
            <a
              v-if="item.href"
              :href="item.href"
              class="cf-closeout__link"
              :data-cy="`cf-closeout-item-${item.key}`"
              >{{ item.title }}</a
            >
            <span
              v-else
              class="cf-closeout__title"
              :data-cy="`cf-closeout-item-${item.key}`"
              >{{ item.title }}</span
            >
            <span
              class="cf-closeout__reason"
              :data-cy="`cf-closeout-reason-${item.key}`"
              >{{ item.reason }}</span
            >
          </li>
        </ul>

        <!-- The overflow routes to the checklist rather than growing the list or
             nesting a scrollbar: the modal is for deciding, the form is for
             doing, and the form already shows every item with its input. -->
        <a
          v-if="group.hidden && group.allHref"
          :href="group.allHref"
          class="cf-closeout__more"
          :data-cy="`cf-closeout-more-${group.id}`"
          >{{ group.hidden }} more in this checklist</a
        >
        <span
          v-else-if="group.hidden"
          class="cf-closeout__more cf-closeout__more--plain"
          :data-cy="`cf-closeout-more-${group.id}`"
          >{{ group.hidden }} more in this checklist</span
        >
      </div>

      <!-- The waiver lives inside the block, not next to the Save button: this is
           where the admin is already looking, and it makes the two courses of
           action adjacent — go fix the items via the links, or knowingly waive
           them here. Ticking it is what turns the reason field into an override
           reason, so waiving is always a deliberate act rather than a side
           effect of typing a note. -->
      <Checkbox
        v-if="overridable"
        class="cf-closeout__override"
        data-cy="cf-closeout-override"
        :model-value="overriding"
        :label="
          overriding
            ? 'Completing anyway — record the reason below'
            : 'Complete anyway and record why'
        "
        @update:model-value="$emit('update:overriding', $event)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import Checkbox from "@/components/Seranova/Checkbox/Checkbox.vue";
import type {
  CustomFormCloseoutGap,
  CustomFormUnmetRequirement
} from "@/typings/customForm";
import { jobFormQuestionUrl } from "./customFormDeepLink";

// Enough to show what kind of work is outstanding without letting one checklist
// push the times and Save button out of view. Past this the checklist itself is
// the better surface.
const MAX_VISIBLE_ITEMS = 3;

const props = withDefaults(
  defineProps<{
    gaps: CustomFormCloseoutGap[];
    // Absent means the items render unlinked rather than pointing nowhere.
    jobId?: number | string | null;
    // Caller-resolved: whether the Forms tab is actually reachable.
    canLink?: boolean;
    // Whether this actor may waive the gate at all. Off means the block is
    // purely informational, as it was before the override existed.
    overridable?: boolean;
    overriding?: boolean;
  }>(),
  { jobId: null, canLink: false, overridable: false, overriding: false }
);

defineEmits<{ "update:overriding": [value: boolean] }>();

const groups = computed(() =>
  props.gaps.map((gap) => {
    // ?? [] because a complete-but-unsubmitted submission may omit the key
    // rather than send an empty array.
    const items = (gap.unmet_requirements ?? []).map((item) => ({
      key: item.question_id,
      title: item.title,
      reason: reasonLabel(item),
      href: linkTo(gap.submission_id, item.question_id)
    }));

    return {
      id: gap.submission_id,
      name: gap.template_name,
      answered: gap.required_answered,
      total: gap.required_total,
      items,
      visible: items.slice(0, MAX_VISIBLE_ITEMS),
      hidden: Math.max(items.length - MAX_VISIBLE_ITEMS, 0),
      allHref: linkTo(gap.submission_id)
    };
  })
);

// Counts the work, not the checklists: an admin wants to know how much is left,
// and a checklist awaiting only its submit contributes none.
const headline = computed(() => {
  const outstanding = groups.value.reduce(
    (total, group) => total + group.items.length,
    0
  );
  const noun = outstanding === 1 ? "item" : "items";

  // Once the waiver is ticked the block is no longer stating a requirement, so it
  // stops saying the visit "can't close" — it says what is about to be waived, and
  // the items stay listed so the decision is made with them in view.
  if (props.overriding) {
    return outstanding
      ? `Completing anyway with ${outstanding} unfinished ${noun}.`
      : "Completing anyway with a checklist that has not been submitted.";
  }

  if (!outstanding) return "Submit the checklists below to close this visit.";

  const verb = outstanding === 1 ? "needs" : "need";
  return `${outstanding} ${noun} still ${verb} attention before this visit can close.`;
});

function linkTo(submissionId: number, questionId?: number): string | null {
  return props.canLink
    ? jobFormQuestionUrl(props.jobId, submissionId, questionId)
    : null;
}

// A photo gap is an ANSWERED question short of its minimum, so it must not read
// as unanswered.
function reasonLabel(item: CustomFormUnmetRequirement): string {
  if (item.reason !== "photos_required") return "not answered";

  const required = item.photos_required ?? 0;
  const noun = required === 1 ? "photo" : "photos";
  return `${item.photos_attached ?? 0} of ${required} ${noun}`;
}
</script>

<style scoped>
/* Matches the tech app's AlertBanner (negative): 2px border, 4px radius, and
   the shared red tokens, so the same block reads the same on both surfaces.
   Type metrics are matched too, but the family is inherited rather than pinned
   to Inter — pinning it would make this the one component in the modal with its
   own typeface. */
.cf-closeout {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px;
  border-radius: 4px;
  border: 2px solid var(--sera-color-accents-red-200);
  background: var(--sera-color-accents-red-100);
}
/* Ticked, the block is a decision the admin has made rather than a wall they
   have hit, so it drops the error palette. Green rather than a neutral grey so
   the change is unmistakable at a glance — the eye is already on this block. */
.cf-closeout--overriding {
  border-color: var(--sera-color-accents-green-200);
  background: var(--sera-color-accents-green-100);
}
.cf-closeout__icon {
  flex: none;
  width: 16px;
  margin-top: 3px;
  text-align: center;
  color: var(--sera-color-fields-error);
}
.cf-closeout--overriding .cf-closeout__icon {
  color: var(--sera-color-accents-green-600);
}
.cf-closeout__override {
  margin-top: 10px;
}
.cf-closeout__body {
  min-width: 0;
  flex: 1;
}
.cf-closeout__headline {
  margin: 0;
  color: var(--sera-color-text-titles);
  font-size: 14px;
  font-weight: 500;
  line-height: 21px;
  letter-spacing: -0.42px;
}
.cf-closeout__group {
  margin-top: 8px;
}
.cf-closeout__group-head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px;
}
.cf-closeout__name {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: -0.39px;
  color: var(--sera-color-text-titles);
}
.cf-closeout__count {
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: var(--sera-color-text-body);
}
.cf-closeout__awaiting,
.cf-closeout__items {
  margin: 2px 0 0;
  padding: 0;
  list-style: none;
}
.cf-closeout__awaiting {
  font-size: 12px;
  color: var(--sera-color-text-body);
}
.cf-closeout__item {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px;
  font-size: 13px;
  line-height: 20px;
}
.cf-closeout__link {
  color: var(--sera-color-text-links);
  text-decoration: underline;
}
.cf-closeout__title {
  color: var(--sera-color-text-titles);
}
/* The reason is the diagnosis, so it stays readable rather than dropping to a
   decorative grey. */
.cf-closeout__reason {
  font-size: 12px;
  color: var(--sera-color-text-body);
}
.cf-closeout__more {
  display: inline-block;
  margin-top: 2px;
  font-size: 12px;
  font-weight: 500;
  color: var(--sera-color-text-links);
  text-decoration: underline;
}
.cf-closeout__more--plain {
  color: var(--sera-color-text-body);
  text-decoration: none;
}
</style>
